
/*!*************************************************************************
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. 
 **************************************************************************/

import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { createC2pa, createL2ManifestStore, generateVerifyUrl } from 'c2pa';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it.
 */
const C2paContext = createContext(null);
function C2paProvider({ config, children }) {
    const [c2paState, setC2paState] = useState(null);
    useEffect(() => {
        function initC2pa() {
            return __awaiter(this, void 0, void 0, function* () {
                const c2pa = yield createC2pa(config);
                setC2paState(c2pa);
            });
        }
        initC2pa();
        return () => {
            c2paState === null || c2paState === void 0 ? void 0 : c2paState.dispose();
        };
    }, []);
    return (React.createElement(C2paContext.Provider, { value: c2paState }, children));
}

/**
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it.
 */
function useC2paContext() {
    const ctxValue = useContext(C2paContext);
    if (process.env.NODE_ENV !== 'production' &&
        typeof ctxValue === 'undefined') {
        throw new Error('C2pa context not available; ensure a <C2paProvider> exists in the component ancestry');
    }
    return ctxValue;
}

/**
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it.
 */
function useC2pa(input) {
    const c2paContext = useC2paContext();
    const [result, setResult] = useState();
    useEffect(() => {
        function read() {
            return __awaiter(this, void 0, void 0, function* () {
                if (c2paContext && input) {
                    const readResult = yield c2paContext.read(input);
                    setResult(readResult);
                }
            });
        }
        read();
    }, [c2paContext, input]);
    return result;
}

/**
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it.
 */
function useThumbnailUrl(thumbnail) {
    const [blobUrl, setBlobUrl] = useState();
    const [hash, setHash] = useState();
    useEffect(() => {
        const computeHash = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const thumbnailHash = yield ((_a = thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.hash) === null || _a === void 0 ? void 0 : _a.call(thumbnail));
            setHash(thumbnailHash);
        });
        computeHash();
    }, [thumbnail]);
    useEffect(() => {
        let disposeFn = () => { };
        if (thumbnail) {
            const { url, dispose } = thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.getUrl();
            setBlobUrl(url);
            disposeFn = dispose;
        }
        return disposeFn;
    }, [hash]);
    return blobUrl;
}

var classnames = {exports: {}};

/*!
  Copyright (c) 2018 Jed Watson.
  Licensed under the MIT License (MIT), see
  http://jedwatson.github.io/classnames
*/

(function (module) {
	/* global define */

	(function () {

		var hasOwn = {}.hasOwnProperty;

		function classNames() {
			var classes = [];

			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (!arg) continue;

				var argType = typeof arg;

				if (argType === 'string' || argType === 'number') {
					classes.push(arg);
				} else if (Array.isArray(arg)) {
					if (arg.length) {
						var inner = classNames.apply(null, arg);
						if (inner) {
							classes.push(inner);
						}
					}
				} else if (argType === 'object') {
					if (arg.toString === Object.prototype.toString) {
						for (var key in arg) {
							if (hasOwn.call(arg, key) && arg[key]) {
								classes.push(key);
							}
						}
					} else {
						classes.push(arg.toString());
					}
				}
			}

			return classes.join(' ');
		}

		if (module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else {
			window.classNames = classNames;
		}
	}());
} (classnames));

var classNames = classnames.exports;

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "/**\n * Copyright 2022 Adobe\n * All Rights Reserved.\n *\n * NOTICE: Adobe permits you to use, modify, and distribute this file in\n * accordance with the terms of the Adobe license agreement accompanying\n * it.\n */\n.L2Image_wrapper__nxCNW {\n  position: relative; }\n  .L2Image_wrapper__nxCNW > img {\n    width: 100%;\n    height: auto; }\n  .L2Image_wrapper__nxCNW cai-popover {\n    position: absolute;\n    top: 10px;\n    right: 10px; }\n";
var styles = {"wrapper":"L2Image_wrapper__nxCNW"};
styleInject(css_248z);

/**
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it.
 */
function L2Image(props) {
    var _a;
    const { srcSet, wcClassName, wrapperClass, wrapperStyle } = props, imgProps = __rest(props, ["srcSet", "wcClassName", "wrapperClass", "wrapperStyle"]);
    if (srcSet) {
        throw new Error('<L2Image> does not support srcSet. Use src instead');
    }
    const [manifestStore, setManifestStore] = useState(null);
    const summaryRef = useRef();
    const c2pa = useC2pa(props.src);
    useEffect(() => {
        var _a;
        let disposeFn = () => { };
        if (!((_a = c2pa === null || c2pa === void 0 ? void 0 : c2pa.manifestStore) === null || _a === void 0 ? void 0 : _a.activeManifest)) {
            return;
        }
        createL2ManifestStore(c2pa.manifestStore).then(({ manifestStore: componentL2ManifestStore, dispose }) => {
            setManifestStore(componentL2ManifestStore);
            disposeFn = dispose;
        });
        return disposeFn;
    }, [(_a = c2pa === null || c2pa === void 0 ? void 0 : c2pa.manifestStore) === null || _a === void 0 ? void 0 : _a.activeManifest]);
    const viewMoreUrl = props.src ? generateVerifyUrl(props.src) : '';
    useEffect(() => {
        const summaryElement = summaryRef.current;
        if (summaryElement && manifestStore) {
            summaryElement.manifestStore = manifestStore;
            summaryElement.viewMoreUrl = viewMoreUrl;
        }
    }, [summaryRef, manifestStore]);
    return (React.createElement("div", { style: wrapperStyle, className: classNames([wrapperClass, styles.wrapper]) },
        React.createElement("img", Object.assign({}, imgProps)),
        manifestStore ? (React.createElement("cai-popover", { interactive: true, class: wcClassName },
            React.createElement("cai-indicator", { slot: "trigger", class: wcClassName }),
            React.createElement("cai-manifest-summary", { ref: summaryRef, slot: "content", class: wcClassName }))) : null));
}

export { C2paProvider, L2Image, useC2pa, useC2paContext, useThumbnailUrl };
